import { createFileRoute } from "@tanstack/react-router";
import { ENV } from "@/env";
import { queryClient } from "@/queryClient";
import { queries as brandingQueries } from "@/services/branding/queries";
import { marketPlaceQueries } from "@/services/marketPlace";
import { z } from "zod";

const searchSchema = z.object({
  path: z.string().optional(),
});

type AppConfig = {
  name: string;
  url: string;
};

const appConfig: Record<string, AppConfig> = {
  dual: {
    name: "Dual",
    url: "https://dual-simulations.com/portal/login",
  },
  capture: {
    name: "Capture",
    url: ENV.API_SERVER,
  },
  interact: {
    name: "Interact",
    url: "https://vyncke-interact.apps.vintecc.cloud",
  },
  smartplant: {
    name: "SmartPlant",
    url: "https://vyncke-smartplant.apps.vintecc.cloud",
  },
  monitor: {
    name: "Monitor",
    url: "https://poppies.apps.dev.vintecc.cloud",
  },
  nodered: {
    name: "Node-RED",
    url: "https://vyncke-nodered.apps.vintecc.cloud",
  },
  noderedui: {
    name: "Node-RED UI",
    url: "https://vyncke-nodered.apps.vintecc.cloud/ui",
  },
};

export const Route = createFileRoute("/app/$id")({
  loader: async ({ params }) => {
    const customApps = await queryClient.ensureQueryData(marketPlaceQueries.getCustomAppsForUser());

    const allApps = (customApps.data ?? []).reduce(
      (acc, app) => ({
        ...acc,
        [app.platformApp!.toLowerCase()]: {
          name: app.platformApp!,
          url: `https://${app.url!}`,
        },
      }),
      appConfig,
    );

    return {
      app: allApps[params.id?.toLowerCase()] ?? null,
    };
  },
  head: ({ loaderData }) => {
    // Root route normally appends platform name for static titles; this route uses
    // a dynamic title, so compose it explicitly with branding here.
    const branding = queryClient.getQueryData<{ platformName?: string | null }>(brandingQueries.getBranding().queryKey);
    const platformName = branding?.platformName?.trim() || "Capture";
    const appName = loaderData?.app?.name?.trim();
    const title = appName ? `${appName} | ${platformName}` : platformName;

    return {
      meta: [
        {
          title,
        },
      ],
    };
  },
  validateSearch: searchSchema,
});
